import { forwardRef, ReactNode, useImperativeHandle, useMemo, useState } from 'react';
import { Form } from 'semantic-ui-react';
import { FormContext, FormMode } from './FormContext';
import { FormActions } from './FormActions';
import { UseFormReturn } from 'react-hook-form';

type FormWrapperProps<TFormValues extends object> = {
  children: ReactNode;
  defaultMode?: FormMode;
  onCancel?: () => void;
  onSubmit: (data: TFormValues) => void;
  isSaving?: boolean;
  isDeleting?: boolean;
  isRestoring?: boolean;
  onDelete?: () => void;
  onRestore?: () => void;
  error?: unknown;
  editPermissions?: number[];
  deletePermissions?: number[];
  restorePermissions?: number[];
  createPermissions?: number[];
  hideActions?: boolean;
  formMethods: UseFormReturn<TFormValues>;
  backUrl?: string;
};

export type FormWrapperHandle = {
  setMode: (mode: FormMode) => void;
  getMode: () => FormMode;
};

function FormWrapperInner<TFormValues extends object>(
  {
    children,
    defaultMode = 'read',
    onCancel,
    onSubmit,
    isSaving = false,
    isDeleting = false,
    isRestoring = false,
    onDelete,
    onRestore,
    error,
    editPermissions,
    deletePermissions,
    restorePermissions,
    createPermissions,
    hideActions = false,
    formMethods,
    backUrl,
  }: FormWrapperProps<TFormValues>,
  ref: React.ForwardedRef<FormWrapperHandle>
) {
  const [mode, setMode] = useState<FormMode>(defaultMode);

  useImperativeHandle(ref, () => ({
    setMode,
    getMode: () => mode,
  }));

  const contextValue = useMemo(
    () => ({
      mode,
      setMode,
      onCancel,
      isSaving,
      isDeleting,
      isRestoring,
      onDelete,
      onRestore,
      error,
      editPermissions,
      deletePermissions,
      restorePermissions,
      createPermissions,
      formMethods,
    }),
    [
      mode,
      setMode,
      onCancel,
      isSaving,
      isDeleting,
      isRestoring,
      onDelete,
      onRestore,
      error,
      editPermissions,
      deletePermissions,
      restorePermissions,
      createPermissions,
      formMethods,
    ]
  );

  return (
    <FormContext.Provider value={contextValue}>
      <Form onSubmit={formMethods.handleSubmit(onSubmit)} noValidate>
        {children}
        {!hideActions && <FormActions backUrl={backUrl} />}
      </Form>
    </FormContext.Provider>
  );
}

export const FormWrapper = forwardRef(FormWrapperInner) as <TFormValues extends object>(
  props: FormWrapperProps<TFormValues> & { ref?: React.ForwardedRef<FormWrapperHandle> }
) => ReturnType<typeof FormWrapperInner>;
